// MobileRoom — the touch-first room surface for the mobile shell.
//
// Assembles the mobile room header (back, name, member count, the room
// `⋯` sheet entry) over the reused virtualised `Timeline` in its mobile
// layout, and owns the long-press `MessageSheet`. The timeline internals
// (grouping, day dividers, reactions, thread summaries, typing
// indicator, composer) are reused — this component adds the mobile
// chrome, the action sheet wiring, and keyboard avoidance.

import { useEffect, useRef, useState, type ReactNode } from "react";

import { useMessageMenu } from "~/components/mobile/MessageMenu";
import type { QuoteComposerHandle } from "~/lib/quote";
import { ChevronLeft, MoreHorizontal } from "lucide-react";

import { BlockDialog } from "~/components/BlockDialog";
import { RoomSheet } from "~/components/mobile/RoomSheet";
import { useMobileStatus } from "~/components/mobile/status";
import { mobileStrings } from "~/components/mobile/strings";
import { Timeline, type TimelineEditController } from "~/components/Timeline";
import { useMatrix } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useKeyboardInset } from "~/hooks/useVisualViewport";
import {
  archiveRoom,
  getRoomNotificationLevel,
  isRoomEncrypted as matrixIsRoomEncrypted,
  setRoomNotificationLevel,
} from "~/lib/matrix";
import type { Client, RoomNotificationLevel, RoomSummary, TimelineEvent } from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

export function roomLabel(room: RoomSummary): string {
  return room.isDirect ? room.name : `#${room.name}`;
}

export function MobileRoomHeader({
  roomId,
  title,
  subtitle,
  onBack,
  onOpenOptions,
  actions,
}: {
  roomId?: string;
  title: string;
  subtitle: string | null;
  /** Omitted in the tablet split pane, where the list stays beside the room. */
  onBack?: () => void;
  onOpenOptions?: () => void;
  actions?: ReactNode;
}) {
  // The connection speaks in the subtitle's place; the header never grows.
  const line = useMobileStatus() ?? subtitle;
  return (
    <div className="flex h-14 shrink-0 items-center gap-1.5 px-2">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          aria-label={mobileStrings.nav.back}
          className="flex size-11 items-center justify-center rounded-md text-muted-foreground outline-none transition-opacity hover:text-foreground/90 focus-visible:shadow-neon-cyan"
        >
          <ChevronLeft size={20} strokeWidth={ICON_STROKE} />
        </button>
      ) : (
        <div className="size-11 shrink-0" aria-hidden="true" />
      )}
      <div className="flex min-w-0 flex-1 flex-col items-center text-center">
        <span
          data-conversation-name={roomId}
          className="truncate text-sm font-light text-foreground/90"
        >
          {title}
        </span>
        {line !== null && (
          <span className="truncate text-xs font-light text-muted-foreground">{line}</span>
        )}
      </div>
      {actions != null && <div className="flex shrink-0 items-center gap-1">{actions}</div>}
      {onOpenOptions ? (
        <button
          type="button"
          onClick={onOpenOptions}
          aria-label={mobileStrings.room.roomOptions}
          className="flex size-11 items-center justify-center rounded-md text-muted-foreground outline-none transition-opacity hover:text-foreground/90 focus-visible:shadow-neon-cyan"
        >
          <MoreHorizontal size={20} strokeWidth={ICON_STROKE} />
        </button>
      ) : (
        <div className="size-11 shrink-0" aria-hidden="true" />
      )}
    </div>
  );
}

export function MobileRoom({
  client,
  room,
  onBack,
  showBack = true,
  onOpenThread,
  headerActions,
}: {
  client: Client;
  room: RoomSummary;
  /** Leaves the room level (back button, and after leave/archive). */
  onBack: () => void;
  /** `false` in the tablet split pane: no back button, the list is visible. */
  showBack?: boolean;
  /** Opens the thread level for a root event (shell owns the stack). */
  onOpenThread: (root: TimelineEvent) => void;
  headerActions?: ReactNode;
}) {
  const { userId, cryptoReady, leaveRoom } = useMatrix();
  const { push: pushToast } = useToast();
  const keyboardInset = useKeyboardInset();
  const editControllerRef = useRef<TimelineEditController | null>(null);
  const quoteComposerRef = useRef<QuoteComposerHandle | null>(null);
  const messageMenu = useMessageMenu({
    room,
    quoteComposerRef,
    openEdit: (eventId) => editControllerRef.current?.openEdit(eventId),
  });
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [notificationLevel, setNotificationLevel] = useState<RoomNotificationLevel>("mentions");
  const [roomEncrypted, setRoomEncrypted] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRoomEncrypted(null);
    void matrixIsRoomEncrypted(client, room.id)
      .then((encrypted) => {
        if (!cancelled) setRoomEncrypted(encrypted);
      })
      .catch(() => {
        if (!cancelled) setRoomEncrypted(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, room.id]);

  const archived = room.archived !== null && room.archived !== undefined;
  const showEncryptedGate = roomEncrypted !== false && !cryptoReady;
  const directPeer =
    room.isDirect && room.directUserIds.length === 1 ? room.directUserIds[0] : null;

  function openOptions() {
    setNotificationLevel(getRoomNotificationLevel(client, room.id));
    setOptionsOpen(true);
  }

  function selectNotificationLevel(level: RoomNotificationLevel) {
    setNotificationLevel(level);
    void setRoomNotificationLevel(client, room.id, level).catch((err) => {
      console.error("setRoomNotificationLevel failed", err);
      setNotificationLevel(getRoomNotificationLevel(client, room.id));
    });
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      style={{
        paddingBottom: keyboardInset > 0 ? keyboardInset : "var(--chat-phone-bottom-inset, 0px)",
      }}
    >
      <MobileRoomHeader
        roomId={room.id}
        title={roomLabel(room)}
        subtitle={mobileStrings.room.members(room.memberCount)}
        onBack={showBack ? onBack : undefined}
        onOpenOptions={openOptions}
        actions={headerActions}
      />
      {showEncryptedGate ? (
        <div
          role="status"
          aria-live="polite"
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-8 text-center"
        >
          <p className="text-sm font-light text-foreground/90">
            {mobileStrings.room.preparingEncrypted}
          </p>
          <p className="text-sm font-light text-muted-foreground">
            {mobileStrings.room.preparingEncryptedBody}
          </p>
        </div>
      ) : (
        <Timeline
          key={`mobile-timeline:${room.id}`}
          client={client}
          roomId={room.id}
          mobile
          composerPlaceholder={mobileStrings.room.composerPlaceholder(roomLabel(room))}
          onOpenThread={onOpenThread}
          onLongPressMessage={messageMenu.open}
          quoteComposerRef={quoteComposerRef}
          editControllerRef={editControllerRef}
          archived={archived}
          archivedReadOnly={archived}
        />
      )}
      {messageMenu.element}
      {blockOpen && directPeer && (
        <BlockDialog
          userId={directPeer}
          name={room.name}
          onClose={() => {
            setBlockOpen(false);
          }}
        />
      )}
      {optionsOpen && (
        <RoomSheet
          roomId={room.id}
          roomLabel={roomLabel(room)}
          memberCount={room.memberCount}
          notificationLevel={notificationLevel}
          onSelectNotificationLevel={selectNotificationLevel}
          onArchive={() => {
            if (!userId) return;
            setOptionsOpen(false);
            void archiveRoom(client, room.id, userId)
              .then(() => onBack())
              .catch((err) => {
                console.error("archiveRoom failed", err);
                pushToast({ message: mobileStrings.home.archiveError(room.name) });
              });
          }}
          onLeave={() => {
            setOptionsOpen(false);
            void leaveRoom(room.id)
              .then(() => onBack())
              .catch((err) => {
                console.error("leaveRoom failed", err);
                pushToast({ message: mobileStrings.roomSheet.leaveError(room.name) });
              });
          }}
          onBlock={
            directPeer
              ? () => {
                  setOptionsOpen(false);
                  setBlockOpen(true);
                }
              : undefined
          }
          onClose={() => setOptionsOpen(false)}
        />
      )}
    </div>
  );
}
